import React from 'react';
import { BORDER, TAN, DocTitle } from './ui';

const Badge = ({ ok }) => (
  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${ok ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
    {ok ? (
      <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>
    ) : (
      <span className="text-xs font-bold">!</span>
    )}
  </span>
);

const Card = ({ title, ok, onEdit, children }) => (
  <div className={`border ${BORDER}`}>
    <div className={`flex items-center justify-between gap-3 ${TAN} px-4 py-2`}>
      <div className="flex items-center gap-2">
        <Badge ok={ok} />
        <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-800">{title}</h4>
      </div>
      <button type="button" onClick={onEdit} className="text-xs font-semibold text-blue-700 hover:underline">Edit</button>
    </div>
    <dl className="grid grid-cols-1 gap-x-8 gap-y-1.5 p-4 text-sm sm:grid-cols-2">{children}</dl>
  </div>
);

const Item = ({ label, value }) => (
  <div className="flex gap-2">
    <dt className="shrink-0 text-gray-500">{label}:</dt>
    <dd className="min-w-0 truncate font-medium text-gray-900">{value || '—'}</dd>
  </div>
);

/** Final step – live summary of what was entered, with jump-back links */
const StepReview = ({ watch, files, goToStep }) => {
  const company = watch('companyInfo') || {};
  const sub = watch('agreement.subscription') || {};
  const owners = (watch('owners') || []).filter((o) => o && o.fullName);
  const payroll = watch('payrollInfo') || {};
  const agreement = watch('agreement') || {};
  const bank = agreement.bankDetails || {};

  const hasControl = owners.some((o) => o.isControlPerson);
  const acct = bank.accountNumber ? `•••• ${String(bank.accountNumber).slice(-4)}` : '';

  const okCompany = !!(company.legalName && company.ein && company.address && sub.initials && sub.signature);
  const okOwners = owners.length > 0 && hasControl;
  const okPayroll = !!(payroll.payType && payroll.payFrequency && payroll.states?.length);
  const okDocs = files.ownerIds.length > 0 && !!files.einLetter && !!files.signedContract;
  const okAgreement = !!(agreement.isSigned && agreement.signature && bank.routingNumber && bank.accountNumber);

  return (
    <div className="space-y-5">
      <DocTitle subtitle="Please review everything before submitting.">Review &amp; Submit</DocTitle>

      <Card title="Company & subscription" ok={okCompany} onEdit={() => goToStep(1)}>
        <Item label="Legal name" value={company.legalName} />
        <Item label="EIN" value={company.ein} />
        <Item label="Address" value={[company.address, company.city, company.state, company.zip].filter(Boolean).join(', ')} />
        <Item label="Signed by" value={sub.printName} />
      </Card>

      <Card title="Ownership" ok={okOwners} onEdit={() => goToStep(2)}>
        {owners.map((o, i) => (
          <Item key={i} label={`Individual ${i + 1}`} value={`${o.fullName} · ${o.ownershipPercentage}%${o.isControlPerson ? ' · Control Person' : ''}`} />
        ))}
        {!owners.length && <Item label="Owners" value="" />}
      </Card>

      <Card title="Payroll information" ok={okPayroll} onEdit={() => goToStep(3)}>
        <Item label="Workers" value={payroll.payType === 'Both' ? 'W-2 & 1099' : payroll.payType} />
        <Item label="Pay period" value={`${payroll.payFrequency || ''} ${payroll.payPeriodStartingDay ? `(starts ${payroll.payPeriodStartingDay})` : ''}`} />
        <Item label="Annual revenue" value={payroll.estimatedAnnualRevenue} />
        <Item label="States" value={(payroll.states || []).join(', ')} />
      </Card>

      <Card title="Documents" ok={okDocs} onEdit={() => goToStep(4)}>
        <Item label="Owner IDs" value={`${files.ownerIds.length} file(s)`} />
        <Item label="EIN letter" value={files.einLetter?.name} />
        <Item label="Client contract" value={files.signedContract?.name} />
      </Card>

      <Card title="Debit authorization" ok={okAgreement} onEdit={() => goToStep(5)}>
        <Item label="Institution" value={bank.institutionName} />
        <Item label="ABA" value={bank.routingNumber} />
        <Item label="Account" value={acct} />
        <Item label="Signed by" value={agreement.signerName} />
      </Card>

      <div className="rounded border border-green-200 bg-green-50/70 p-4">
        <h4 className="text-sm font-bold text-gray-900">Setup fee paid ✓</h4>
        <p className="mt-1 text-sm text-gray-600">
          When you submit, your completed form and documents are sent to Everee automatically.
        </p>
      </div>
    </div>
  );
};

export default StepReview;
